const getDataByKey = (searchPath, key, module) => {
    if (module[searchPath]) {
        return module[searchPath]
            .filter((dataByKey) => dataByKey.type === key && dataByKey.content)
            .map((dataByContent) => dataByContent.content);
    }
    return [];
};

const isResourceAlreadyLoaded = (url, dom) => url && dom.getElementById(url);

/**
 * 
 * @param {css or js url} url 
 * @param {type of tag} elemType ("link"/"script")
 * Dispatches an event: if there are multiple <tags>, getting rendered. We send request for merch css and js only once every unique url
 * 
 * Creates an global object, kind of like an directory of loaded resources.
 * If in future we have some placements, that starts rendering on-scroll or on-click, then we need to know before hand, if css and js was loaded or not
 * 
 * example object created:
 * window {
 *      ebay: {
 *          ads: {
 *              assets: {
 *                  link: ['merch.css'],
 *                  script: ['merch.js', 'scandal.js']
 *              }
 *          }
 *      }
 * }
 */
const registerLoadingOfURLS = (url, elemType) => {
    document.dispatchEvent(new Event(`resource-loaded-${url}`));
    if(window.ebay) {
        if(window.ebay.ads) {
            if(window.ebay.ads.assets) {
                if(!window.ebay.ads.assets[elemType]) {
                    window.ebay.ads.assets[elemType] = [url]
                } else {
                    window.ebay.ads.assets[elemType].push(url);
                }
            } else {
                window.ebay.ads.assets = {
                    [elemType]: [url]
                };
            }
        } else {
            window.ebay.ads = {
                assets: {
                    [elemType]: [url]
                }
            };
        }
    } else {
        window.ebay = {
            ads: {
                assets: {
                    [elemType]: [url]
                }
            }
        };
    }
};

const renderCSSAndJSURL = (url, elemType, attributes, dom) => new Promise((resolve) => {
    if (!isResourceAlreadyLoaded(url, dom)) {
        const tag = dom.createElement(elemType);
        tag.setAttribute('id', url);
        tag.onload = () => {
            registerLoadingOfURLS(url, elemType);
            resolve();
        };
        tag.onerror = () => {
            resolve();
        };
        Object.keys(attributes).forEach((key) => {
            tag.setAttribute(key, attributes[key]);
        });
        if (elemType === 'link') {
            tag.href = url;
        } else {
            tag.src = url;
        }
        dom.head.appendChild(tag);
    } else if((url && (((((window || {}).ebay || {}).ads || {}).assets || {})[elemType] || []).find((loadedAssetUrl) => loadedAssetUrl === url)) || !url) {
       // element was already loaded.
       resolve();
    } else if(url){
        document.addEventListener(`resource-loaded-${url}`, () => {
            resolve();
        }, { once: true });
    }
});

const renderCssInlineAndHTML = (html, cssinline, docId, dom) => {
    return new Promise((resolve) => {
        const clientPlacementDoc = dom.getElementById(docId);
        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(() => {
            observer.disconnect();
            resolve();
        });
        if (clientPlacementDoc) {
            observer.observe(clientPlacementDoc, {
                attributes: false,
                childList: true,
                subtree: true,
            });
            clientPlacementDoc.innerHTML = html;
            clientPlacementDoc.style = cssinline;
        } else {
            resolve();
        }
    });
}

const renderInlineScript = (text, dom) => {
    const newScript = dom.createElement('script');
    newScript.type = 'text/javascript';
    newScript.text = text;
    dom.head.appendChild(newScript);
};

const executeHTMLDOCScript = (docId, dom) => {
    const doc = dom.getElementById(docId);
    if (doc) {
        [...doc.getElementsByTagName('script')].forEach((script) => renderInlineScript(script.text, dom));
    }
};

const activateModMerch = () => {
    if (typeof $_mod_merch !== 'undefined') {
        // eslint-disable-next-line no-undef
        $_mod_merch.ready();
    }
};

const render = async (module, docId, dom, skeletonId) => {
    const cssURL = getDataByKey('css', 'CSS_URL', module);
    let promises = [];
    cssURL.forEach((url) => {
        promises.push(renderCSSAndJSURL(url, 'link', {
            rel: 'stylesheet',
            type: 'text/css',
        }, dom));
    });
    // All CSS can be called in parallel
    await Promise.all(promises);
    const cssInline = getDataByKey(
        'css',
        'CSS_INLINE',
        module
    ).reduce((acc, curr) => `${curr} ${acc}`, '').trim();

    const html = (module.html && module.html.content) ? module.html.content : '<div />';
    await renderCssInlineAndHTML(html, cssInline, docId, dom);
    const skeletonDoc = document.getElementById(skeletonId);
    if(skeletonDoc) {
        skeletonDoc.style.display = 'none';
    }
    
    promises = [];
    const jsURL = getDataByKey('js', 'JS_URL', module);
    jsURL.forEach((url) => {
        promises.push(renderCSSAndJSURL(url, 'script', {
            type: 'text/javascript',
        }, dom));
    });
    // All JS can be called in parallel
    await Promise.all(promises);

    getDataByKey('js', 'JS_INLINE', module)
        .forEach((text) => {
            renderInlineScript(text, dom);
        });
    executeHTMLDOCScript(docId, dom);
    activateModMerch();
    window.dispatchEvent(new Event(`deli-response-rendered-${docId}`));
}

const renderReady = (module) => {
    const loading =
    module && module.loading
            ? module.loading
            : "IMMEDIATE";
    return new Promise((resolve) => {
        switch (loading) {
            case "IMMEDIATE":
                return resolve();
            case "DOCUMENT_READY": {
                if (document.readyState !== "loading") {
                    return resolve();
                } else {
                    document.addEventListener(
                        "DOMContentLoaded",
                        () => {
                            return resolve();
                        },
                        { once: true }
                    );
                }
                break;
            }
            case "WINDOW_LOAD": {
                if (document.readyState === "complete") {
                    return resolve();
                } else {
                    window.addEventListener(
                        "load",
                        () => {
                            return resolve();
                        },
                        { once: true }
                    );
                }
                break;
            }
            case "WINDOW_LOAD_DELAYED": {
                if (document.readyState === "complete") {
                    return resolve();
                } else {
                    window.addEventListener(
                        "load",
                        () => {
                            return resolve();
                        },
                        { once: true }
                    );
                }
                break;
            }
            default:
                return resolve();
        }
    });
};

module.exports = {
    renderReady,
    render
};
